<script lang="ts">
import { defineComponent as __MACROS_defineComponent } from "vue";
export default /*#__PURE__*/ __MACROS_defineComponent({
  inheritAttrs: false,
});
</script>
<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import { useSlots } from 'vue';

export type SiffletButtonVariant = 'elevated' | 'outlined' | 'text';
export type SiffletButtonColor = 'primary' | 'secondary' | 'danger' | 'ai' | 'jira' | 'slack' | 'selected' | 'servicenow' | 'siffletBrandPrimary';
export type SiffletButtonSize = 'x-small' | 'small' | 'default' | 'large';
export type SiffletButtonIconAlignment = 'left' | 'right';

export interface SButtonProps {
  /**
   * Async Action to be executed with loading state. If provided, the button will be in loading state until being executed. Not linked to @click in your instanciated component.
   *
   */
  asyncAction?: () => Promise<any>
  /**
   * Route to navigate to.
   */
  to?: string | object | null
  /**
   * Whether the button is disabled or not.
   */
  disabled?: boolean
  /**
   * Text to be displayed in the button.
   */
  text?: string
  /**
   * Tooltip to be displayed when hovering the button.
   */
  tooltip?: string | null
  /**
   * Icon to be displayed in the button.
   */
  icon?: string | null
  /**
   * Button size (`x-small`, `small`, `default` or `large`). `default` by default.
   */
  size?: SiffletButtonSize
  /**
   * Button variant (`elevated`, `outlined` or `text`). `elevated` by default.
   */
  variant?: SiffletButtonVariant
  /**
   * Button color (`primary`, `secondary`, `danger` or `ai`). `primary` by default.
   */
  color?: SiffletButtonColor
  /**
   * Icon alignment (`left` or `right`). `left` by default.
   */
  iconAlignment?: SiffletButtonIconAlignment
  /**
   * data-track attribute for testing purposes.
   */
  dataTrack?: string
  /**
   * Whether the button is in beta or not.
   */
  beta?: boolean
}
const props = defineProps({
  asyncAction: { type: Function },
  to: null,
  disabled: { type: Boolean, default: false },
  text: null,
  tooltip: null,
  icon: null,
  size: { default: 'default' },
  variant: { default: 'elevated' },
  color: { default: 'primary' },
  iconAlignment: { default: 'left' },
  dataTrack: null,
  beta: { type: Boolean, default: false }
});
type Emits = {
  /**
   * Click event emitted when the button is clicked.
   */
  (event: 'click', e: MouseEvent): void
}
const emits = defineEmits(["click"]);
;

const slots = useSlots();

let loading = _ref(false);

const vuetifyColor = _computed(() => {
  switch (props.color) {
    case 'primary':
      return 'primary';
    case 'secondary':
      return 'secondary';
    case 'danger':
      return 'alert';
    case 'ai':
      return 'iconAi';
    case 'jira':
      return 'jira';
    case 'servicenow':
      return 'servicenow';
    case 'siffletBrandPrimary':
      return 'siffletBrandPrimary';
    default:
      return 'primary';
  }
});

const classes = _computed(() => {
  const result = [];
  result.push(`v-btn--color-${props.color}`);
  if ((props.icon && !props.text) || (slots.icon && !props.text)) {
    result.push('icon-only');
  }
  return result;
});

const isExternalLink = _computed(() => {
  if (!props.to) return false;

  return props.to.toString().startsWith('http');
});

const computedBindings = _computed(() => ({
  to: !isExternalLink.value ? props.to : null,
  href: isExternalLink.value ? props.to : null,
  target: isExternalLink.value ? '_blank' : null,
}));

const click = async (event: MouseEvent) => {
  if (!props.to) {
    event.preventDefault();
  }
  if (props.asyncAction) {
    loading.value = true;
    try {
      await props.asyncAction();
    } finally {
      loading.value = false;
    }
  } else {
    emits('click', event);
  }
};
</script>

<template lang="pug">
//- This div is required to make the SButton layout to work properly with classes
div(v-if="tooltip || slots.tooltip")
  v-tooltip(top)
    template(#activator='{ on }')
      //- This div is required for the v-tooltip to show up
      div(v-on="on")
        v-btn(
          :disabled='disabled'
          @click.stop='click'
          @keydown.enter.stop='click'
          v-bind="computedBindings"
          :color='color'
          :class="classes"
          :text='variant === "text" || variant === "outlined"'
          :outlined='variant === "outlined"'
          :loading='loading'
          :small='size === "small"'
          :large='size === "large"'
          :x-small='size === "x-small"'
          :ripple='false'
          :data-track='dataTrack')
          SIcon(v-if="icon && iconAlignment === 'left'" :class="{'mr-2': !!text}" :size="size" :icon="icon")
          slot(name="icon" v-if="iconAlignment === 'left'")

          | {{ text }}
          v-chip.ml-2(v-if="props.beta" x-small outlined :color="vuetifyColor") {{ $t('common.words.beta') }}
          SIcon(v-if="icon && iconAlignment === 'right'" :class="{'ml-2': !!text}" :size="size" :icon="icon")
          slot(name="icon" v-if="iconAlignment === 'right'")
    slot(name="tooltip")
      div(v-if="props.tooltip") {{ props.tooltip }}
v-btn(
  v-else
  :disabled='disabled'
  @click.stop='click'
  @keydown.enter.stop='click'
  v-bind="computedBindings"
  :color='color'
  :class="classes"
  :text='variant === "text" || variant === "outlined"'
  :outlined='variant === "outlined"'
  :loading='loading'
  :small='size === "small"'
  :large='size === "large"'
  :x-small='size === "x-small"'
  :ripple='false'
  :data-track='dataTrack')
  SIcon(v-if="icon && iconAlignment === 'left'" :class="{'mr-2': !!text}" :size="size" :icon="icon")
  slot(name="icon" v-if="iconAlignment === 'left'")
  | {{ text }}
  v-chip.ml-2(v-if="props.beta" x-small outlined :color="vuetifyColor") {{ $t('common.words.beta') }}
  SIcon(v-if="icon && iconAlignment === 'right'" :class="{'ml-2': !!text}" :size="size" :icon="icon")
  slot(name="icon" v-if="iconAlignment === 'right'")
</template>

<style scoped lang="scss">
.v-btn__content {
  padding: 0 !important;  /* Removes any padding around the content */
  margin: 0 !important;   /* Removes any margin around the content */
}
</style>
